import TableOnly from "components/DataTable/TableOnly";
import { approvalBadgeTone } from "utils/approvalStage";
import RefreshButton from "components/RefreshButton/RefreshButton";
import Pagination from "components/Pagination/Pagination";
import QuickViewWorkOrder from "components/QuickViewWorkOrder/QuickViewWorkOrder";
import SearchComp from "components/SearchComp/SearchComp";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
// import * as XLSX from "xlsx";
import WOAdvanceFormLayout from "./AdvanceSearch/WOAdvanceFormLayout";
import WorkOrdersAdd from "./WorkOrdersAdd";
import LineItemReport from "./LineItemReport/LineItemReport";
import { useWoStatuses, woStatusLabel, woStatusStyle } from "utils/woStatus";
import PendingInvoicesTabs from "components/PendingInvoicesTabs";
import {
  convertAttendanceRecordToTime,
  convertSqlTimestampToDate,
  formatSqlDate,
} from "utils/UtilityFuncs";

function WorkOrders() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();

  /*   All States
   ********************************************* */
  const [workOrders, setWorkOrders] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [showQuickView, setShowQuickView] = useState(false);

  const [active, setActive] = useState(null);
  // Tabs = All + Settings -> WO Statuses, in its order (user, 2026-10-29).
  // "invoice-ready" is the Pending Invoices page: its own sidebar item, no tabs.
  const woStatuses = useWoStatuses();
  const listTabs = [
    { value: "all", label: "All" },
    ...woStatuses.map((s) => ({ value: s.value, label: s.name })),
  ];
  const [limit, setLimit] = useState(0);

  const [locationsList, setLocationsList] = useState([]);
  const [showLineItemReport, setShowLineItemReport] = useState(false);
  const [location, setLocation] = useState("");

  const [totalPages, setTotalPages] = useState(1);

  const [isLoading, setIsLoading] = useState(null);

  const [showJobNameModal, setShowJobNameModal] = useState(false);
  const [jobNamesFormData, setJobNamesFormData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Due-date filtering now lives inside Build A Find (dueDateFrom/dueDateTo),
  // which replaced the standalone date-range control that used to sit above
  // this list.
  const [advanceSearchModal, setAdvanceSearchModal] = useState(false);
  // Non-null means a find is active and its results replace the paged list.
  const [findResult, setFindResult] = useState(null);

  /*   All Functions
   ********************************************* */
  const changeLimit = (val) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", val);
      return newParams.toString();
    });
  };

  const changeLocation = (val) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (val && val !== "none") {
        newParams.set("location", val);
      } else {
        newParams.delete("location");
      }
      return newParams.toString();
    });
  };

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  function getWOBodyData(searchOptions, activeTab) {
    let page = searchOptions.get("page") || 1;
    let pageLimit = searchOptions.get("limit") || 10;
    let query = searchOptions.get("query");
    let location = searchOptions.get("location");
    let body = { page, activeTab, pageLimit };

    if (query) {
      body.query = query;
    }
    if (location) {
      body.location = location;
    }

    return body;
  }

  const getWorkOrders = async (activeTab) => {
    setIsLoading(true);
    try {
      let body = getWOBodyData(searchParams, activeTab);

      if (body.location === "undefined" || body.location === "null") {
        delete body.location;
      }

      // check for sort and order in searchParams
      let sort = searchParams.get("sort");
      let order = searchParams.get("order");
      if (sort && order) {
        body.sort = sort;
        body.order = order;
      }
      const { data } = await API.post("/work-orders-list", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setWorkOrders(data.result);
        setTotalPages(data.pages);
        setIsLoading(false);
        return data.result;
      } else {
        throw new Error(data?.message || "Failed to fetch work orders");
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setIsLoading(false);
    }
  };

  // Delete WorkOrders
  const deleteWorkOrder = async (id) => {
    setIsLoading(true);
    try {
      const { data } = await API.delete("/work-orders/delete/" + id, {
        headers: authHeader(),
      });
      if (data?.message?.type === "success") {
        await getWorkOrders(active);
        return;
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setIsLoading(false);
    }
  };

  // Getting 12 Hours Time from SQL timestamp
  // const convertSqlTimestampToDate = (sqlTime) => {
  //   const timestamp = sqlTime;
  //   const date = new Date(timestamp);
  //   const time = date.toLocaleString("en-US", {
  //     year: "numeric",
  //     month: "long",
  //     day: "numeric",
  //   });
  //   return time;
  // };

  const handleJobNameDoubleClick = (item) => {
    setSelectedItem(item);
    setJobNamesFormData({
      jobName: item.jobName,
      subJobName: item.subJobName,
    });
    setShowJobNameModal(true);
  };

  const UpdateJobNames = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.patch(
        "/work-orders",
        {
          data: {
            jobName: jobNamesFormData.jobName,
            subJobName: jobNamesFormData.subJobName,
          },
          id: selectedItem.id,
        },
        { headers: authHeader() },
      );

      if (data.message.type === "success") {
        await getWorkOrders();

        setIsLoading(false);
        setShowJobNameModal(false);
        return;
      }
      setShowJobNameModal(false);
      setIsLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setIsLoading(false);
    }
  };

  const getAllLocations = async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocationsList(data);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getSearchParamsWithoutGivenKey = (key) => {
    let params = new URLSearchParams(searchParams);
    params.delete(key);

    return params.toString();
  };

  const handleViewCustomer = (id) => {
    navigate(`/customer/details/${id}`);
  };

  // Get initial letters of location name
  const getLocationInitials = (id, name) => {
    if (!name) return "";
    const words = name.split(" ");
    let initials = words.map((word) => word.charAt(0).toUpperCase());
    return `${id}-${initials.join("")}`;
  };

  /*   All useEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllLocations();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!params?.filterBy) {
      navigate("/work-orders/all");
      setActive("all");
    } else {
      setActive(params?.filterBy);
    }

    // if (params?.filterBy === "pending" || params?.filterBy === "processing") {
    //   setActive(params?.filterBy);
    // } else {
    //   setActive("all");
    // }
  }, [params]);

  // Only re-fetch the list when a list-relevant param changes. QuickView
  // writes `selected-work-order` to the URL on every prev/next click, and
  // clears it on close -- neither should trigger a refetch of the page's
  // work orders (the list itself hasn't changed).
  const listParamsKey = [
    searchParams.get("page") ?? "",
    searchParams.get("limit") ?? "",
    searchParams.get("query") ?? "",
    searchParams.get("location") ?? "",
    searchParams.get("sort") ?? "",
    searchParams.get("order") ?? "",
  ].join("|");

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (active) {
        getWorkOrders(active);
      }
      // location
      let locationId = searchParams.get("location");
      if (locationId) {
        setLocation(locationId);
      } else {
        setLocation("");
      }
      // Limit
      let pageLimit = searchParams.get("limit");
      if (pageLimit) {
        setLimit(pageLimit);
      } else {
        changeLimit(10);
        setLimit(10);
      }
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, listParamsKey]);

  /*   Table Functions
   ********************************************* */
  const ActionButtons = () => (
    <div>
      <div className="d-flex align-items-center gap-1 text-nowrap">
        {/* Find sits outside the work_orders_add gate, matching the Quotes
            page — searching the list isn't a create action. */}
        <button
          onClick={() => setAdvanceSearchModal(true)}
          type="button"
          className="btn btn-info text-white"
        >
          Find
        </button>
        {findResult && (
          <button
            onClick={() => setFindResult(null)}
            type="button"
            className="btn btn-outline-danger ms-1 text-nowrap"
          >
            Clear Find
          </button>
        )}
        {(user?.permissions?.includes("work_orders_add") ||
          user?.roles === "admin") && (
          <div className="d-flex align-items-center gap-1 text-nowrap">
          <div className="d-flex align-items-center gap-2">
            <h4>Location</h4>
            <select
              onChange={(e) => {
                changeLocation(e.target.value);
              }}
              className="form-control"
              value={location || "none"}
            >
              <option value="none">All Locations</option>
              <option value="no-location">No Location</option>
              {locationsList?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item?.locationName}
                </option>
              ))}
            </select>
          </div>
          {/* <button
            onClick={() => downloadExcel(workOrders)}
            type="button"
            className="btn btn-primary btn-success"
          >
            Export Data
          </button> */}
          <RefreshButton
            className="btn btn-outline-primary me-1"
            loading={isLoading}
            onClick={() => getWorkOrders(active)}
          />
          <button
            onClick={() => setShowLineItemReport(true)}
            type="button"
            className="btn btn-outline-primary me-1 text-nowrap"
            title="Total quantities for a style, by team, over a date range"
          >
            <i className="mdi mdi-chart-box-outline"></i> Line Item Report
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            type="button"
            className="btn btn-primary "
          >
            Add Work Order
          </button>
            {/* <button
            onClick={TestFunc}
            type="button"
            className="btn btn-primary btn-success"
          >
            Test Func
          </button> */}
          </div>
        )}
      </div>
    </div>
  );


  // ONE decision, taken in utils/approvalStage.js from the stored stage;
  // the wording and classes below stay local because the screens differ.
  // This used to re-derive the stage from the approval cards right here --
  // a second definition, free to disagree with the Approvals tab and with
  // the other copy of this same function.
  const getApprovalStatus = (item) => {
    switch (approvalBadgeTone(item)) {
      case "rejected":
        return { text: "Rejected", className: "bg-danger" };
      case "approved":
        return { text: "Approved", className: "bg-success" };
      case "partial":
        return {
          text: "Partially",
          className: "border border-success border-3 text-dark",
          style: { backgroundColor: "#ffff00" },
        };
      case "not_sent":
        return { text: "Not sent", className: "bg-secondary" };
      default:
        return { text: "Pending", className: "bg-info" };
    }
  };

  const table_head = [
    { name: "W-ID", value: "woId" },
    { name: "Customer Name", value: "customerName" },
    { name: "Job Name", value: "jobName" },
    { name: "Sub Job", value: "subJobName" },
    { name: "Created By", value: "createdBy" },
    { name: "Location", value: "locationId" },
    { name: "Due Date", value: "WoDueDate" },
    { name: "Created At", value: "createdAt" },
    { name: "Last Sent", value: "approvalSentTime" },
    { name: "Status", value: "WOStatus" },
    { name: "Approval", value: "approvalStatus" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => {
    const approvalStatus = getApprovalStatus(item);

    return (
      <tr className="" key={item.id}>
        <td className="py-1 align-middle me-0 text-nowrap">W-{item.woId}</td>
        <td className="py-1 align-middle me-0 text-nowrap w-25">
          <div className="d-flex align-items-center">
            <button
              onClick={() => handleViewCustomer(item?.customer?.customerId)}
              className="btn btn-sm btn-info py-0 px-1 text-white me-2"
            >
              View
            </button>
            <div>{item.customerName?.slice(0, 30)}</div>
          </div>
        </td>
        <td
          onDoubleClick={() => handleJobNameDoubleClick(item)}
          className="py-1 align-middle text-nowrap me-0 w-25"
        >
          {item.jobName?.slice(0, 30)}
        </td>
        <td
          onDoubleClick={() => handleJobNameDoubleClick(item)}
          className="py-1 align-middle text-nowrap me-0 "
          style={{
            width: "10%",
          }}
        >
          {item.subJobName?.slice(0, 30)}
        </td>
        <td
          className="py-1 align-middle me-0 text-truncate text-nowrap"
          style={{
            width: "10%",
          }}
        >
          {item.createdBy}
        </td>
        <td className="py-1 align-middle me-0">
          {getLocationInitials(item?.locationId, item?.location?.name)}
        </td>
        <td className="py-1 align-middle me-0 text-nowrap">
          {formatSqlDate({ sqlDate: item.WoDueDate, hideYear: true })}
        </td>
        <td className="align-middle py-1 text-nowrap">
          {item?.createdAt
            ? `${convertSqlTimestampToDate(item.createdAt, true, true)} - ${convertAttendanceRecordToTime(
                item.createdAt,
                false,
              )}`
            : "-"}
        </td>
        <td className="align-middle py-1 text-nowrap">
          {item?.woLotApproval?.approvalSentTime
            ? `${convertSqlTimestampToDate(item?.woLotApproval?.approvalSentTime, true, true)} - ${convertAttendanceRecordToTime(
                item?.woLotApproval?.approvalSentTime,
                false,
              )}`
            : "-"}
        </td>

        <td className="py-1 align-middle me-0">
          <span
            className="badge px-2 py-1"
            style={woStatusStyle(woStatuses, item?.WOStatus)}
          >
            {woStatusLabel(woStatuses, item?.WOStatus)}
          </span>
        </td>
        <td className="py-1 align-middle me-0">
          <span
            className={`badge px-2 py-1 ${approvalStatus.className}`}
            style={approvalStatus.style}
          >
            {approvalStatus.text}
          </span>
        </td>

        <td className="table-action py-1 me-0">
          <div className="d-flex align-items-center">
            <div
              onClick={(e) => {
                setShowQuickView(true);
                updateSearchParams("selected-work-order", item?.woId);
              }}
            >
              <button className="btn btn-sm bg-success text-white text-nowrap">
                Quick View
              </button>
            </div>
            <button
              onClick={() => {
                navigate(
                  `/work-order/details/${active}/${
                    item?.woId
                  }?${searchParams.toString()}`,
                );
              }}
              className={`btn btn-sm bg-success text-white mx-2 `}
            >
              Details
            </button>

            {(window.location.hostname === "localhost" ||
              window.location.hostname === "127.0.0.1") && (
              <button
                onClick={() => {
                  deleteWorkOrder(item?.id);
                }}
                className="btn btn-danger"
              >
                <i className="mdi mdi-trash-can"></i>
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    if (user && user?.defaultLocation && locationsList?.length) {
      if (searchParams.get("location")) return;

      let _selectedLocation = locationsList?.find(
        (item) => String(item.id) === String(user.defaultLocation),
      );

      if (_selectedLocation?.id) {
        changeLocation(String(_selectedLocation.id));
      }
    }
  }, [user, locationsList, searchParams]);

  return (
    <div className="row">
      <QuickViewWorkOrder
        // selectedId={item?.woId}
        workOrdersList={workOrders}
        refreshFunc={getWorkOrders}
        active={active}
        show={showQuickView}
        setShow={setShowQuickView}
        updateSearchParams={updateSearchParams}
        btnClasses={"btn btn-sm bg-success text-white"}
      />
      {workOrders ? (
        <div className="col-12">
          <div className="d-flex">
            <div className=" flex-grow-1">
              {/* Tabs */}
              <div
                className={`flex-grow-1 d-flex flex-column bg-white shadow-sm px-5 py-2`}
                style={{
                  position: "fixed",
                  top: 0,
                  width: "calc(100vw - 200px)",
                  marginLeft: 200,
                  zIndex: "99",
                }}
              >
                {active === "invoice-ready" ? (
                  <PendingInvoicesTabs active="w-orders" />
                ) : (
                <ul className="nav nav-tabs nav-bordered border-0 d-flex flex-grow-1">
                  {listTabs.map(({ value, label }) => (
                    <li
                      key={value}
                      onClick={() =>
                        navigate(
                          `/work-orders/${value}?` +
                            getSearchParamsWithoutGivenKey("page") +
                            "&page=1",
                        )
                      }
                      className="nav-item"
                    >
                      <button
                        className={
                          active === value
                            ? `btn btn-primary active bg-primary text-white`
                            : "nav-link"
                        }
                      >
                        <span className="d-none d-md-block">{label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                )}
              </div>
              {/* Table */}
              <div className="">
                <>
                  <div
                    className=" px-3 py-2 pb-2 horizontal-scrollable py-4 d-flex flex-column flex-grow-1 mt-5"
                    style={{ borderRadius: 10, marginLeft: 260 }}
                  >
                    <div className="d-flex justify-content-end align-items-center mb-3">
                      <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-1">
                          <div>Show</div>
                          <button
                            className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {limit}
                          </button>
                          <ul
                            style={{ width: "fit-content" }}
                            className="dropdown-menu"
                          >
                            <li
                              onClick={() => {
                                changeLimit(5);
                              }}
                            >
                              <button className="dropdown-item" href="#">
                                5
                              </button>
                            </li>
                            <li
                              onClick={() => {
                                changeLimit(10);
                              }}
                            >
                              <button className="dropdown-item" href="#">
                                10
                              </button>
                            </li>
                            <li
                              onClick={() => {
                                changeLimit(15);
                              }}
                            >
                              <button className="dropdown-item" href="#">
                                15
                              </button>
                            </li>
                            <li
                              onClick={() => {
                                changeLimit(20);
                              }}
                            >
                              <button className="dropdown-item" href="#">
                                20
                              </button>
                            </li>
                          </ul>
                        </div>
                        <div className="me-1">{ActionButtons()}</div>
                      </div>
                      <SearchComp
                        activeTab={active}
                        setLoading={setIsLoading}
                        setTotalPages={setTotalPages}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                        apiPath={`/work-orders/${active}?${getSearchParamsWithoutGivenKey(
                          "query",
                        )}&`}
                      />
                    </div>
                    {findResult && (
                      <div className="alert alert-info py-1 px-2 mb-2">
                        Showing <strong>{findResult.length}</strong> find result
                        {findResult.length === 1 ? "" : "s"}. Use{" "}
                        <strong>Clear Find</strong> to go back to the full list.
                      </div>
                    )}
                    <TableOnly
                      table_head={table_head}
                      table_body={table_body}
                      table_data={findResult || searchResults || workOrders}
                      totalPages={totalPages}
                    />
                    {isLoading && <Spinner />}
                    {/* The find returns its whole result set in one response,
                        so paging it against the list's page count would be
                        wrong -- hide the pager while a find is active. */}
                    {!findResult && !searchResults && (
                      <Pagination
                        totalPages={totalPages}
                        apiPath={`/work-orders/${active}?${
                          searchParams.toString()
                            ? "?" + getSearchParamsWithoutGivenKey("page") + "&"
                            : "?"
                        }page=`}
                        currPage={parseInt(searchParams.get("page")) || 1}
                      />
                    )}
                  </div>
                </>
              </div>
            </div>
            {/*   Line Item Report -- reuses this page's locations list
             ********************************************* */}
            <LineItemReport
              show={showLineItemReport}
              onHide={() => setShowLineItemReport(false)}
              locations={locationsList}
            />

            {/*   Build A Find Modal
             ********************************************* */}
            <Modal
              show={advanceSearchModal}
              onHide={() => setAdvanceSearchModal(false)}
              size="xl"
              scrollable
              contentClassName="bg-white shadow-lg custom-border-radius"
            >
              {/* No Modal.Body wrapper here on purpose -- the layout supplies
                  its own Header/Body/Footer so that `scrollable` pins the
                  header and the Search button while only the fields scroll. */}
              <WOAdvanceFormLayout
                showModal={setAdvanceSearchModal}
                setSearchResult={setFindResult}
              />
            </Modal>

            {/* Update JobName & SubJobName modal
             ********************************************* */}

            <Modal
              size="lg"
              show={showJobNameModal}
              onHide={() => setShowJobNameModal(false)}
            >
              <Modal.Body>
                <form onSubmit={UpdateJobNames}>
                  {isLoading && <Spinner />}
                  <h4>Update Job Name / Sub Job Name for W-{selectedItem?.woId}</h4>
                  <hr />
                  <div className="my-3">
                    <label>Job Name</label>
                    <input
                      className="form-control"
                      value={jobNamesFormData?.jobName || ""}
                      onChange={(e) =>
                        setJobNamesFormData((prev) => ({
                          ...prev,
                          jobName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="my-3">
                    <label>Sub Job Name</label>
                    <input
                      className="form-control"
                      value={jobNamesFormData?.subJobName || ""}
                      onChange={(e) =>
                        setJobNamesFormData((prev) => ({
                          ...prev,
                          subJobName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <hr className="mt-4" />
                  <div className="text-end">
                    <button
                      className="btn btn-sm btn-secondary me-2"
                      onClick={() => setShowJobNameModal(false)}
                    >
                      Close
                    </button>
                    <button className="btn btn-sm btn-primary">Update</button>
                  </div>
                </form>
              </Modal.Body>
            </Modal>

            {/*   ADD Modal
             ********************************************* */}
            <Modal
              size="lg"
              show={showAddModal}
              onHide={() => setShowAddModal(false)}
            >
              <Modal.Body>
                <div>
                  <WorkOrdersAdd showAddModal={setShowAddModal} />
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
}

export default WorkOrders;
